import { createIcons, icons } from 'lucide';

const FALLBACK_APP_LOGIN_URL = '/v2/login';

// --- Billing provider selection ---
// VITE_BILLING_PROVIDER determines which pricing API to call.
// 'paddle' (default) — fetches from /api/billing/plans (Paddle)
// 'polar' — fetches from /api/billing/polar/plans (Polar.sh)
const SUPPORTED_BILLING_PROVIDERS = new Set(['paddle', 'polar']);
const configuredBillingProvider = (import.meta.env.VITE_BILLING_PROVIDER || 'paddle').trim().toLowerCase();
const BILLING_PROVIDER = SUPPORTED_BILLING_PROVIDERS.has(configuredBillingProvider)
    ? configuredBillingProvider
    : 'paddle';

if (configuredBillingProvider !== BILLING_PROVIDER && import.meta.env.MODE === 'development') {
    console.warn(`public-pricing: unsupported VITE_BILLING_PROVIDER "${configuredBillingProvider}"; using Paddle.`);
}

// --- DataLayer Event Helper Function (local definition) ---
function sendDataLayerEvent(eventName, eventCategory, eventLabel, eventValue) {
  if (typeof window.dataLayer === 'object') {
    window.dataLayer.push({
      'event': eventName, // GTM will use this for Custom Event Triggers
      'event_category': eventCategory,
      'event_label': eventLabel,
      'value': eventValue // Optional
    });
  } else {
    if (import.meta.env.MODE === 'development') { console.warn('window.dataLayer object not found in public-pricing.js. Event not sent.'); }
  }
}

const RAW_API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').trim();

function resolveSignupUrl() {
    const rawLoginUrl = (import.meta.env.VITE_APP_LOGIN_URL || FALLBACK_APP_LOGIN_URL).trim();

    try {
        const signupUrl = new URL(rawLoginUrl, window.location.origin);
        signupUrl.searchParams.set('mode', 'signup');
        return signupUrl.toString();
    } catch (error) {
        if (import.meta.env.MODE === 'development') {
            console.warn('public-pricing: invalid VITE_APP_LOGIN_URL, falling back to local /v2/login:', error?.message || error);
        }

        return `${FALLBACK_APP_LOGIN_URL}?mode=signup`;
    }
}

function resolvePublicApiUrl(path) {
    if (!RAW_API_BASE_URL) {
        return path;
    }

    const normalizedBase = RAW_API_BASE_URL.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
}

// ---------------------------------------------------------------------------
// Plan fetching — provider-aware
// ---------------------------------------------------------------------------

async function fetchSubscriptionPlans() {
    const endpoint = BILLING_PROVIDER === 'polar'
        ? '/api/billing/polar/plans'
        : '/api/billing/plans';

    try {
        const response = await fetch(resolvePublicApiUrl(endpoint));
        if (!response.ok) {
            console.error(`Error fetching plans: ${response.status} ${response.statusText}`);
            const errorBody = await response.text();
            if (import.meta.env.MODE === 'development') { console.error(`Error body: ${errorBody}`); }
            return { data: [], error: `Failed to load subscription plans. Status: ${response.status}` };
        }
        const plans = await response.json();
        if (!plans || !Array.isArray(plans.data)) {
            console.error('Pricing API returned an invalid response shape.');
            return { data: [], error: 'Subscription plans are temporarily unavailable.' };
        }

        const validPlans = plans.data.filter(plan => plan && Array.isArray(plan.prices) && plan.prices.length > 0);
        if (validPlans.length !== plans.data.length) {
            if (import.meta.env.MODE === 'development') { console.warn("Some plans were filtered out due to missing or invalid price structures."); }
        }
        return { data: validPlans, error: null };
    } catch (error) {
        console.error('Network error fetching plans:', error.message || 'Unknown network error'); if (import.meta.env.MODE === 'development') { console.error('Full error details:', error); }
        return { data: [], error: 'Network error when trying to load plans.' };
    }
}

// ---------------------------------------------------------------------------
// Normalization — maps both Paddle and Polar shapes to a common format
// ---------------------------------------------------------------------------

/**
 * Normalize a plan from either provider into a common internal shape:
 * {
 *   id, name, description, isFreeTier,
 *   metadata: { minutes, premiumVoice, webhooks, mcp, allowCrawl, scrapePageLimit },
 *   price: { amountCents, currencyCode, isRecurring }
 * }
 */
function normalizePlan(rawPlan) {
    if (BILLING_PROVIDER === 'polar') {
        return normalizePolarPlan(rawPlan);
    }
    return normalizePaddlePlan(rawPlan);
}

function normalizePaddlePlan(plan) {
    const monthlyPriceInfo = plan.prices?.find(p => p.billing_cycle?.interval === 'month');
    const amountCents = parseNonNegativeInteger(monthlyPriceInfo?.unit_price?.amount);
    const currencyCode = monthlyPriceInfo?.unit_price?.currency_code || 'USD';

    if (!plan.isFreeTier && amountCents === null) {
        return null;
    }

    return {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        isFreeTier: !!plan.isFreeTier,
        metadata: {
            minutes: parseNonNegativeInteger(plan.custom_data?.minutes),
            premiumVoice: parseBoolean(plan.custom_data?.premium_voice),
            webhooks: parseBoolean(plan.custom_data?.webhooks),
            mcp: parseBoolean(plan.custom_data?.mcp),
            allowCrawl: parseBoolean(plan.custom_data?.crawl),
            scrapePageLimit: parseNonNegativeInteger(plan.custom_data?.scrape),
        },
        price: {
            amountCents,
            currencyCode: normalizeCurrencyCode(currencyCode),
            isRecurring: !!monthlyPriceInfo?.billing_cycle,
        },
    };
}

function normalizePolarPlan(plan) {
    if (!plan.is_recurring) {
        return null;
    }

    const fixedPrice = plan.prices?.find(price => {
        const amountType = String(price?.amount_type || '').toLowerCase();
        const hasValidAmount = parseNonNegativeInteger(price?.price_amount) !== null;

        // Polar fixed-price SDK models have changed discriminator aliases
        // across versions. A valid numeric amount remains sufficient for this
        // display-only catalog when the backend omits amount_type.
        return hasValidAmount && (!amountType || amountType === 'fixed');
    });
    const amountCents = parseNonNegativeInteger(fixedPrice?.price_amount);

    if (amountCents === null) {
        return null;
    }

    return {
        id: plan.id,
        name: plan.name,
        description: plan.description,
        isFreeTier: false, // Polar plans from the API are all paid; free tier is synthetic
        metadata: {
            minutes: parseNonNegativeInteger(plan.metadata?.minutes),
            premiumVoice: parseBoolean(plan.metadata?.premium_voice),
            webhooks: parseBoolean(plan.metadata?.webhooks),
            mcp: parseBoolean(plan.metadata?.mcp),
            allowCrawl: parseBoolean(plan.metadata?.allow_crawl),
            scrapePageLimit: parseNonNegativeInteger(plan.metadata?.scrape_page_limit),
        },
        price: {
            amountCents,
            currencyCode: normalizeCurrencyCode(fixedPrice?.price_currency),
            isRecurring: true,
        },
    };
}

// ---------------------------------------------------------------------------
// Rendering helpers (operate on normalized plans)
// ---------------------------------------------------------------------------

function parseBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.trim().toLowerCase());
    return false;
}

function parseNonNegativeInteger(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function normalizeCurrencyCode(value) {
    const currencyCode = String(value || 'USD').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(currencyCode) ? currencyCode : 'USD';
}

function formatPrice(amountCents, currencyCode) {
    if (amountCents === 0) return 'Free';

    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amountCents / 100);
    } catch {
        return `${currencyCode} ${(amountCents / 100).toFixed(2)}`;
    }
}

function isPremiumVoicePlan(normalizedPlan) {
    return normalizedPlan.metadata.premiumVoice;
}

function getVoiceTierDescriptionHTML(tier) {
    if (tier === 'premium') {
        return `
            <div class="rounded-lg border border-teal-100 bg-teal-50 p-5 text-left text-sm text-teal-900">
                <div class="flex flex-wrap items-center gap-2 mb-2">
                    <span class="text-base font-semibold text-teal-900">Premium Voice</span>
                    <span class="text-xs font-medium text-teal-700">Powered by gpt-realtime-2</span>
                </div>
                <p class="m-0 leading-relaxed">
                    Designed for higher-complexity customer service and technical support environments. Adds deeper
                    reasoning for multi-step troubleshooting, longer sessions, specialized terminology, and workflows
                    that depend on connected tools.
                </p>
            </div>
        `;
    }

    return `
        <div class="rounded-lg border border-gray-200 bg-gray-50 p-5 text-left text-sm text-gray-600">
            <div class="flex flex-wrap items-center gap-2 mb-2">
                <span class="text-base font-semibold text-gray-900">Basic Voice</span>
                <span class="text-xs font-medium text-gray-500">Powered by gpt-realtime-2-mini</span>
            </div>
            <p class="m-0 leading-relaxed">
                Ideal for businesses needing a fast, reliable digital receptionist. Optimized for everyday website
                enquiries, FAQ handling, routing, straightforward lead capture, and high-volume standard traffic.
            </p>
        </div>
    `;
}

function createPublicPlanCard(normalizedPlan) {
    const planCard = document.createElement('div');
    planCard.className = 'border border-gray-200 rounded-lg p-8 shadow-sm flex flex-col h-full bg-white';

    let priceDisplay = 'N/A';
    if (normalizedPlan.isFreeTier) {
        priceDisplay = 'Free';
    } else if (normalizedPlan.price.amountCents != null) {
        priceDisplay = formatPrice(normalizedPlan.price.amountCents, normalizedPlan.price.currencyCode);
    }

    const premiumVoice = isPremiumVoicePlan(normalizedPlan);
    const voiceMinuteLabel = premiumVoice ? 'Premium Voice Minutes' : 'Basic Voice Minutes';
    const minutes = normalizedPlan.metadata.minutes || 'N/A';
    const descriptionText = normalizedPlan.description || (normalizedPlan.isFreeTier ? `Perfect for trying out Babelbeez.` : `Includes ${minutes} Voice Minutes per month.`);

    const featuresList = [];
    if (normalizedPlan.isFreeTier) {
        featuresList.push('Easy Setup');
        featuresList.push('3 Voice Agents');
        featuresList.push('Multilingual');
        featuresList.push('20 Basic Voice Minutes');
        featuresList.push('10 Webpages');
        featuresList.push('Full Site Crawl');
        featuresList.push('Automations (Trial)');
        featuresList.push('MCP Apps (Trial)');
    } else {
        const meta = normalizedPlan.metadata;
        if (meta.minutes !== null && meta.minutes > 0) {
            featuresList.push(`${meta.minutes} ${voiceMinuteLabel}`);
        }
        if (premiumVoice) {
            featuresList.push('Premium Voice');
        }
        if (meta.scrapePageLimit !== null && meta.scrapePageLimit > 0) {
            featuresList.push(`${meta.scrapePageLimit} Webpage${meta.scrapePageLimit > 1 ? 's' : ''}`);
        }
        if (meta.allowCrawl) {
            featuresList.push('Full Site Crawl');
        }
        if (meta.webhooks) {
            featuresList.push('Automations');
        }
        if (meta.mcp) {
            featuresList.push('MCP Apps');
        }
    }

    const premiumBadgeHTML = premiumVoice
        ? '<span class="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white">Premium Voice</span>'
        : '';

    planCard.innerHTML = `
        <div class="mb-6 text-left">
            <div class="flex flex-col gap-2 min-h-[9rem]">
                <div class="flex flex-wrap items-center gap-2">
                    <h3 class="plan-name text-2xl font-bold mb-0"></h3>
                    ${premiumBadgeHTML}
                </div>
                <p class="plan-description text-gray-500 text-sm"></p>
            </div>
            <p class="plan-price text-4xl font-bold text-teal-600 mt-4"></p>
        </div>
        <div class="mb-8 flex-grow">
            <p class="text-gray-700 text-sm font-semibold mb-3 text-left">Includes:</p>
            <ul class="plan-features space-y-2 text-left"></ul>
        </div>
        <div class="mt-auto">
            <a class="plan-select-button w-full block text-center px-6 py-2 rounded-md font-medium transition duration-150 ease-in-out"></a>
        </div>
    `;

    const planName = normalizedPlan.name || 'Unnamed Plan';
    planCard.querySelector('.plan-name').textContent = planName;
    planCard.querySelector('.plan-description').textContent = descriptionText;

    const priceElement = planCard.querySelector('.plan-price');
    priceElement.textContent = priceDisplay;
    if (!normalizedPlan.isFreeTier && normalizedPlan.price.isRecurring) {
        const billingPeriod = document.createElement('span');
        billingPeriod.className = 'text-lg font-normal text-gray-500';
        billingPeriod.textContent = '/mo';
        priceElement.append(' ', billingPeriod);
    }

    const featuresElement = planCard.querySelector('.plan-features');
    featuresList.forEach(feature => {
        const item = document.createElement('li');
        item.className = 'flex items-center text-sm text-gray-600';
        item.innerHTML = '<i data-lucide="check" class="w-4 h-4 text-green-500 mr-2 shrink-0"></i>';
        item.append(document.createTextNode(feature));
        featuresElement.appendChild(item);
    });

    const button = planCard.querySelector('.plan-select-button');
    button.href = resolveSignupUrl();
    button.dataset.planName = planName;
    button.dataset.planId = normalizedPlan.id || '';
    button.textContent = normalizedPlan.isFreeTier ? 'Start Trial' : `Choose ${planName}`;
    button.classList.add(...(normalizedPlan.isFreeTier
        ? ['bg-gray-100', 'hover:bg-gray-200', 'text-gray-800']
        : ['bg-teal-600', 'hover:bg-teal-700', 'text-white']));

    return planCard;
}

function attachPlanSelectTracking(root) {
    const planSelectButtons = root.querySelectorAll('.plan-select-button');
    planSelectButtons.forEach(button => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            const planName = button.dataset.planName;
            sendDataLayerEvent('pricing_interaction', 'Pricing_Table', 'Select_Plan_Click', planName);
            window.location.href = resolveSignupUrl();
        });
    });
}

function renderPublicPricingTable(allNormalizedPlans) {
    const container = document.getElementById('dynamic-pricing-table-container');
    if (!container) {
        console.error('Pricing table container #dynamic-pricing-table-container not found.');
        return;
    }
    container.innerHTML = '';

    if (!allNormalizedPlans || allNormalizedPlans.length === 0) {
        container.innerHTML = '<p class="text-red-500 italic col-span-full text-center py-10">No pricing plans are currently available.</p>';
        return;
    }

    const premiumOnly = container.hasAttribute('data-premium-only');

    const freePlans = allNormalizedPlans.filter(plan => plan.isFreeTier);
    const paidPlans = allNormalizedPlans.filter(plan => !plan.isFreeTier);
    const basicPlans = paidPlans.filter(plan => !isPremiumVoicePlan(plan));
    const premiumPlans = paidPlans.filter(plan => isPremiumVoicePlan(plan));

    // Premium-only mode: show only premium plans, no free tier, no tabs
    if (premiumOnly) {
        const wrapper = document.createElement('div');
        wrapper.className = 'col-span-full grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch max-w-5xl mx-auto';

        const tierDescription = document.createElement('div');
        tierDescription.className = 'col-span-full';
        tierDescription.innerHTML = getVoiceTierDescriptionHTML('premium');

        wrapper.appendChild(tierDescription);

        if (premiumPlans.length === 0) {
            wrapper.innerHTML += '<p class="text-gray-500 italic col-span-full text-center py-8">No Premium Voice plans are currently available.</p>';
        } else {
            premiumPlans.forEach(plan => wrapper.appendChild(createPublicPlanCard(plan)));
            attachPlanSelectTracking(wrapper);
        }

        container.appendChild(wrapper);
        createIcons({ icons });
        return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'col-span-full grid grid-cols-1 lg:grid-cols-4 gap-8 items-stretch';

    let freeSection = null;
    if (freePlans.length > 0) {
        freeSection = document.createElement('div');
        freeSection.className = 'grid grid-cols-1 w-full';
        freeSection.appendChild(createPublicPlanCard(freePlans[0]));
        attachPlanSelectTracking(freeSection);
    }

    if (paidPlans.length > 0) {
        const paidSection = document.createElement('div');
        paidSection.className = 'lg:col-span-3 flex flex-col gap-6';

        const tabRow = document.createElement('div');
        tabRow.className = 'col-span-full flex justify-center';

        const tabControls = document.createElement('div');
        tabControls.className = 'inline-flex self-center rounded-full border border-gray-200 bg-gray-50 p-1';
        tabControls.setAttribute('role', 'tablist');
        tabControls.setAttribute('aria-label', 'Voice plan tiers');

        const tierDescription = document.createElement('div');

        const paidGrid = document.createElement('div');
        paidGrid.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 h-full';

        let activeTier = basicPlans.length > 0 ? 'basic' : 'premium';

        const basicButton = document.createElement('button');
        basicButton.type = 'button';
        basicButton.textContent = 'Basic Voice';
        basicButton.setAttribute('role', 'tab');

        const premiumButton = document.createElement('button');
        premiumButton.type = 'button';
        premiumButton.textContent = 'Premium Voice';
        premiumButton.setAttribute('role', 'tab');

        const updateTabs = () => {
            const activeClass = 'bg-white text-teal-700 shadow-sm';
            const inactiveClass = 'text-gray-600 hover:text-teal-700';
            basicButton.className = `px-5 py-2 rounded-full text-sm font-semibold transition ${activeTier === 'basic' ? activeClass : inactiveClass}`;
            premiumButton.className = `px-5 py-2 rounded-full text-sm font-semibold transition ${activeTier === 'premium' ? activeClass : inactiveClass}`;
            basicButton.setAttribute('aria-selected', String(activeTier === 'basic'));
            premiumButton.setAttribute('aria-selected', String(activeTier === 'premium'));
            basicButton.disabled = basicPlans.length === 0;
            premiumButton.disabled = premiumPlans.length === 0;
            basicButton.classList.toggle('opacity-50', basicButton.disabled);
            basicButton.classList.toggle('cursor-not-allowed', basicButton.disabled);
            premiumButton.classList.toggle('opacity-50', premiumButton.disabled);
            premiumButton.classList.toggle('cursor-not-allowed', premiumButton.disabled);

            tierDescription.innerHTML = getVoiceTierDescriptionHTML(activeTier);

            paidGrid.innerHTML = '';
            const visiblePlans = activeTier === 'premium' ? premiumPlans : basicPlans;
            if (visiblePlans.length === 0) {
                paidGrid.innerHTML = `<p class="text-gray-500 italic col-span-full text-center py-8">No ${activeTier === 'premium' ? 'Premium' : 'Basic'} Voice plans are currently available.</p>`;
            } else {
                visiblePlans.forEach(plan => paidGrid.appendChild(createPublicPlanCard(plan)));
            }
            attachPlanSelectTracking(paidGrid);
            createIcons({ icons });
        };

        basicButton.addEventListener('click', () => {
            activeTier = 'basic';
            updateTabs();
        });

        premiumButton.addEventListener('click', () => {
            activeTier = 'premium';
            updateTabs();
        });

        tabControls.appendChild(basicButton);
        tabControls.appendChild(premiumButton);
        tabRow.appendChild(tabControls);
        wrapper.appendChild(tabRow);
        if (freeSection) wrapper.appendChild(freeSection);
        paidSection.appendChild(tierDescription);
        paidSection.appendChild(paidGrid);
        wrapper.appendChild(paidSection);
        updateTabs();
    } else if (freeSection) {
        wrapper.appendChild(freeSection);
    }

    container.appendChild(wrapper);
    createIcons({ icons });
}


async function loadAndDisplayPublicPricing() {
    const container = document.getElementById('dynamic-pricing-table-container');
    if (container && !container.querySelector('p.text-red-500')) { 
      container.innerHTML = '<p class="text-gray-500 italic col-span-full text-center py-10">Loading pricing plans...</p>';
    }

    const { data: paidPlansFromAPI, error: paidPlansError } = await fetchSubscriptionPlans();

    if (paidPlansError) {
        if (container) {
            container.innerHTML = `<p class="text-red-500 italic col-span-full text-center py-10">${paidPlansError}</p>`;
        }
        return;
    }
    
    const freePlan = {
        id: 'free-plan',
        name: 'Get Started',
        description: 'Full platform access. Test our voice agents and build your automations with webhooks before you commit.',
        custom_data: { minutes: '20' }, // Ensure minutes is here for feature list
        prices: [{ 
            id: 'free-price',
            billing_cycle: { interval: 'month' },
            unit_price: { amount: '0', currency_code: 'USD' } 
        }],
        isFreeTier: true
    };
    
    // Normalize paid plans from the API
    const processedPaidPlans = paidPlansFromAPI
        .map(plan => normalizePlan(plan))
        .filter(Boolean);
    
    // Normalize the synthetic free plan (always Paddle-shaped since we construct it)
    const normalizedFreePlan = normalizePaddlePlan(freePlan);

    const allPlans = [normalizedFreePlan, ...processedPaidPlans];

    allPlans.sort((a, b) => {
        const priceAAmount = a.isFreeTier ? 0 : a.price.amountCents;
        const priceBAmount = b.isFreeTier ? 0 : b.price.amountCents;

        if (!a.isFreeTier && (priceAAmount === undefined || priceAAmount === null)) return 1;
        if (!b.isFreeTier && (priceBAmount === undefined || priceBAmount === null)) return -1;
        
        const priceA = parseInt(priceAAmount, 10) || 0;
        const priceB = parseInt(priceBAmount, 10) || 0;
        
        const priceDifference = priceA - priceB;
        if (priceDifference !== 0) return priceDifference;
        return String(a.name || '').localeCompare(String(b.name || ''));
    });

    renderPublicPricingTable(allPlans);
}

// Self-initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadAndDisplayPublicPricing);
} else {
    loadAndDisplayPublicPricing();
}